// Atomic utility to apply Redux animation state to Three.js scene objects
// Usage: applyAnimationsToSceneObjects(scene, animationObjects, currentTime)

import * as THREE from 'three';
import { DEFAULT_ANIMATION_SETTINGS } from '../../components/AddComponents/constants/animationConstants';
import {
  applyPlayModelEffect,
  stopPlayModelForObject
} from '../../components/AddComponents/AnimationComponent/handlers/animationEffectHandlers';
import {
  restoreAnimatedObjectMaterials,
  getParticleConfiguredOpacity,
} from './materialOpacitySnapshot';
import { syncLightHelperForSceneObject, syncDescendantLightHelpers } from './lightHelperUtils';
import {
  applyScaleEffectToObject,
} from '../../components/gridpagefunction/Text';

/**
 * Linearly interpolates between two values.
 */
function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Helper: Add outline to a mesh
function addOutline(sceneObj) {
  if (!sceneObj.userData._hasOutline) {
    // Simple outline using a second mesh with slightly larger scale and a basic material
    const outlineMaterial = new THREE.MeshBasicMaterial({
      color: 0xffff00,
      side: THREE.BackSide,
    });
    if (sceneObj.geometry) {
      const outlineMesh = new THREE.Mesh(
        sceneObj.geometry.clone(),
        outlineMaterial
      );
      outlineMesh.scale.multiplyScalar(1.05);
      outlineMesh.name = '__outline';
      sceneObj.add(outlineMesh);
      sceneObj.userData._hasOutline = true;
    }
  }
}

// Helper: Remove outline from a mesh
function removeOutline(sceneObj) {
  if (sceneObj.userData._hasOutline) {
    const outline = sceneObj.getObjectByName('__outline');
    if (outline) sceneObj.remove(outline);
    sceneObj.userData._hasOutline = false;
  }
}

// Easing helper
function getEasedT(easing, t) {
  switch (easing) {
    case 'Ease-in':
      return t * t;
    case 'Ease-out':
      return t * (2 - t);
    case 'In-out':
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    case 'None':
    default:
      return t;
  }
}

/**
 * Resets all animated objects to their original positions and stops animations.
 * @param {THREE.Scene} scene - The Three.js scene.
 * @param {Array} animationObjects - Array of animation objects from Redux.
 */
export function resetAnimationsToOriginalState(
  scene,
  animationObjects,
  options = {}
) {
  const { restoreTransforms = true, restoreMaterials = true } = options;
  if (!scene || !Array.isArray(animationObjects)) return;
  // Helper to compare position objects
  const positionsEqual = (a, b) => {
    if (!a || !b) return false;
    return (
      Number(a.x) === Number(b.x) &&
      Number(a.y) === Number(b.y) &&
      Number(a.z) === Number(b.z)
    );
  };
  animationObjects.forEach((animObj) => {
    const sceneObj = scene.getObjectByName(animObj.name || animObj.id);
    if (animObj?.isActive === false) {
      if (sceneObj) {
        stopPlayModelForObject(sceneObj);
        removeOutline(sceneObj);
      }
      return;
    }
    if (!sceneObj) return;

    // Stop any playModel/GLB animations when resetting (e.g. leaving preview)
    try {
      stopPlayModelForObject(sceneObj);
    } catch (error) {
      console.warn('stopPlayModelForObject failed during animation reset', error);
    }

    if (restoreTransforms) {
      if (sceneObj.userData?.originalPosition) {
        sceneObj.position.copy(sceneObj.userData.originalPosition);
      }
      if (sceneObj.userData?.originalRotation) {
        sceneObj.rotation.copy(sceneObj.userData.originalRotation);
      }
      if (sceneObj.userData?.originalScale) {
        sceneObj.scale.copy(sceneObj.userData.originalScale);
      }
      if (sceneObj.userData?.framePosition && sceneObj.userData?.originalPosition) {
        sceneObj.userData.framePosition.copy(sceneObj.userData.originalPosition);
      }
      if (sceneObj.userData?.frameRotation && sceneObj.userData?.originalRotation) {
        sceneObj.userData.frameRotation.copy(sceneObj.userData.originalRotation);
      }
      if (sceneObj.userData?.frameScale && sceneObj.userData?.originalScale) {
        sceneObj.userData.frameScale.copy(sceneObj.userData.originalScale);
      }

      syncLightHelperForSceneObject(scene, sceneObj);
      syncDescendantLightHelpers(sceneObj, scene);
    }

    if (!restoreMaterials) {
      removeOutline(sceneObj);
      return;
    }

    // Restore material opacity (fade in/out mid-animation)
    if (!restoreAnimatedObjectMaterials(sceneObj)) {
      const timeline = animObj.timelineEffects || animObj.timeline;
      const fadeEffect = timeline?.find((e) =>
        ['fadein', 'fadeout', 'fade'].includes(e.effectType)
      );
      if (fadeEffect) {
        const configuredOp = getParticleConfiguredOpacity(sceneObj);
        const base = configuredOp !== undefined ? configuredOp : 1;
        let targetOpacity = base;
        if (fadeEffect.effectType === 'fadein') {
          targetOpacity =
            fadeEffect.endOpacity !== undefined ? fadeEffect.endOpacity : base;
        } else if (fadeEffect.effectType === 'fadeout') {
          targetOpacity =
            fadeEffect.startOpacity !== undefined
              ? fadeEffect.startOpacity
              : base;
        }
        sceneObj.traverse((child) => {
          if (!child.material) return;
          const mats = Array.isArray(child.material)
            ? child.material
            : [child.material];
          mats.forEach((mat) => {
            mat.transparent = true;
            mat.opacity = targetOpacity;
            if (mat.uniforms?.alpha) {
              mat.uniforms.alpha.value = targetOpacity;
            }
            mat.needsUpdate = true;
          });
        });
      }
    }

    // Check for animations in timelineEffects or timeline
    const timeline = animObj.timelineEffects || animObj.timeline;
    if (!timeline || timeline.length === 0) {
      removeOutline(sceneObj);
      return;
    }

    if (restoreTransforms) {
      const firstEffect = timeline[0];
      if (
        firstEffect &&
        firstEffect.startPosition &&
        !positionsEqual(
          firstEffect.startPosition,
          DEFAULT_ANIMATION_SETTINGS.startPosition
        )
      ) {
        const activeAxes = firstEffect.activeAxes || { x: true, y: true, z: true };
        if (activeAxes.x) sceneObj.position.x = firstEffect.startPosition.x;
        if (activeAxes.y) sceneObj.position.y = firstEffect.startPosition.y;
        if (activeAxes.z) sceneObj.position.z = firstEffect.startPosition.z;
      }

      if (firstEffect && firstEffect.startRotation) {
        const activeAxes =
          firstEffect.activeAxes ||
          firstEffect.rotationAxis || { x: true, y: true, z: true };
        if (activeAxes.x) sceneObj.rotation.x = firstEffect.startRotation.x;
        if (activeAxes.y) sceneObj.rotation.y = firstEffect.startRotation.y;
        if (activeAxes.z) sceneObj.rotation.z = firstEffect.startRotation.z;
      }

      if (firstEffect && firstEffect.startScale) {
        applyScaleEffectToObject(sceneObj, firstEffect, 0);
      }
    }

    syncLightHelperForSceneObject(scene, sceneObj);
    syncDescendantLightHelpers(sceneObj, scene);
    removeOutline(sceneObj);
  });
}

/**
 * Applies animation state to scene objects.
 * @param {THREE.Scene} scene - The Three.js scene.
 * @param {Array} animationObjects - Array of animation objects from Redux.
 * @param {number} currentTime - Current animation time (seconds).
 */
export function applyAnimationsToSceneObjects(
  scene,
  animationObjects,
  currentTime
) {
  if (!scene || !Array.isArray(animationObjects)) return;

  // Track which objects are animated this frame
  const animatedNames = new Set();

  animationObjects.forEach((animObj) => {
    if (animObj?.isActive === false) return;

    const sceneObj = scene.getObjectByName(animObj.name || animObj.id);
    if (!sceneObj) return;

    // Check for animations in timelineEffects or timeline
    const timeline = animObj.timelineEffects || animObj.timeline;
    if (!timeline || timeline.length === 0) return;

    // Find the current active effect on the timeline for this time.
    // Prefer the latest-starting active effect when multiple are present.
    let effect = timeline
      .filter((eff) => currentTime >= eff.startTime && currentTime <= eff.endTime)
      .sort((a, b) => (b.startTime || 0) - (a.startTime || 0))[0];

    // If no active effect found, check for looping effects and choose the most recent.
    if (!effect) {
      effect = timeline
        .filter((eff) => eff.loop && currentTime >= eff.startTime)
        .sort((a, b) => (b.startTime || 0) - (a.startTime || 0))[0];
    }

    if (!effect) {
      stopPlayModelForObject(sceneObj);
      syncDescendantLightHelpers(sceneObj, scene);
      removeOutline(sceneObj);
      return;
    }

    // playModel: sync GLB clip progress to timeline time; render loop calls mixer.update(delta)
    if (effect.effectType === 'playModel') {
      let t;
      if (effect.loop && currentTime >= effect.startTime) {
        const duration = effect.endTime - effect.startTime;
        const loopTime = (currentTime - effect.startTime) % duration;
        t = duration > 0 ? loopTime / duration : 0;
      } else {
        const duration = effect.endTime - effect.startTime;
        t = duration > 0 ? (currentTime - effect.startTime) / duration : 0;
      }
      t = getEasedT(effect.easing || 'None', Math.min(1, Math.max(0, t)));
      applyPlayModelEffect(sceneObj, effect, t);
      syncDescendantLightHelpers(sceneObj, scene);
      addOutline(sceneObj);
      animatedNames.add(sceneObj.name);
      return;
    }

    // Other effects: stop playModel if it was running, then interpolate transform
    stopPlayModelForObject(sceneObj);

    // Calculate normalized time (0 to 1) within the effect
    let t;
    if (effect.loop && currentTime >= effect.startTime) {
      // For looping effects, calculate progress within the loop cycle
      const duration = effect.endTime - effect.startTime;
      const loopTime = (currentTime - effect.startTime) % duration;
      t = duration > 0 ? loopTime / duration : 0;
    } else {
      // For non-looping effects, use normal progress calculation
      t =
        (currentTime - effect.startTime) / (effect.endTime - effect.startTime);
    }

    // Apply easing if specified
    const easing = effect.easing || 'None';
    t = getEasedT(easing, t);

    // Interpolate position
    if (effect.startPosition && effect.endPosition) {
      const activeAxes = effect.activeAxes || { x: true, y: true, z: true };
      if (activeAxes.x) sceneObj.position.x = lerp(effect.startPosition.x, effect.endPosition.x, t);
      if (activeAxes.y) sceneObj.position.y = lerp(effect.startPosition.y, effect.endPosition.y, t);
      if (activeAxes.z) sceneObj.position.z = lerp(effect.startPosition.z, effect.endPosition.z, t);
    }
    // Interpolate rotation
    if (effect.startRotation && effect.endRotation) {
      const activeAxes = effect.activeAxes || effect.rotationAxis || { x: true, y: true, z: true };
      if (activeAxes.x) sceneObj.rotation.x = lerp(effect.startRotation.x, effect.endRotation.x, t);
      if (activeAxes.y) sceneObj.rotation.y = lerp(effect.startRotation.y, effect.endRotation.y, t);
      if (activeAxes.z) sceneObj.rotation.z = lerp(effect.startRotation.z, effect.endRotation.z, t);
    }
    // Interpolate scale
    if (effect.startScale && effect.endScale) {
      applyScaleEffectToObject(sceneObj, effect, t);
    }

    syncDescendantLightHelpers(sceneObj, scene);

    // Add outline for visual feedback
    addOutline(sceneObj);
    animatedNames.add(sceneObj.name);
  });

  // Remove outlines from objects that are no longer animated
  scene.traverse((obj) => {
    if (
      obj.type === 'Mesh' &&
      obj.userData._hasOutline &&
      !animatedNames.has(obj.name)
    ) {
      removeOutline(obj);
    }
  });
}
